import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { 
  Shield, ArrowRight, ArrowLeft, CheckCircle, Loader2, 
  User, Building2, FileWarning, AlertTriangle, Eraser
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const FORM_STEPS = [
  { id: 'type', title: 'Type', icon: User },
  { id: 'applicant', title: 'Applicant Info', icon: User },
  { id: 'employer', title: 'Employer', icon: Building2 },
  { id: 'disclosures', title: 'Disclosures', icon: FileWarning },
  { id: 'attestation', title: 'Sign & Submit', icon: CheckCircle },
];

const DISCLOSURE_QUESTIONS = [
  {
    id: 'q1',
    question: 'Do you have any criminal charges pending against you, including in federal, state, local, military, and Tribal courts?',
    detail: 'If yes, describe the charge and indicate the name of the court, the state, city, month and year you were charged.'
  },
  {
    id: 'q2',
    question: 'Were you ever convicted of any crime anywhere, including in federal, state, local, military, and Tribal courts, or in another country?',
    detail: 'If yes, describe the crime and indicate the name of the court, the state, city, month and year you were convicted.'
  },
  {
    id: 'q3',
    question: 'Has a government or regulatory agency (other than the police) ever found that you abused or neglected a child?',
    detail: 'If yes, describe the conduct and indicate the agency that made the finding, the state, city, month and year of the finding.'
  },
  {
    id: 'q4',
    question: 'Has a government or regulatory agency (other than the police) ever found that you abused or neglected an adult?',
    detail: 'If yes, describe the conduct and indicate the agency that made the finding, the state, city, month and year of the finding.'
  },
  {
    id: 'q5',
    question: 'Has a government or regulatory agency (other than the police) ever found that you stole or misappropriated a person\'s property, identity, or financial information?',
    detail: 'If yes, describe the conduct and indicate the agency that made the finding, the state, city, month and year it occurred.'
  },
  {
    id: 'q6',
    question: 'Do you have a government issued credential that is not current or has been revoked, suspended, or limits you from providing care?',
    detail: 'If yes, identify the type of credential and indicate the credentialing agency, the restriction, and the state, city, month and year it was issued.'
  },
  {
    id: 'q7',
    question: 'Has a government or regulatory agency ever denied, revoked, or limited your license, certification, or registration to provide care, treatment, or educational services?',
    detail: 'If yes, indicate the license type, issuing agency, description of the denial/revocation/limitation, state, city, month and year.'
  },
  {
    id: 'q8',
    question: 'Has a government or regulatory agency ever denied, revoked or limited your ability to live on the premises of a facility that provides care or treatment?',
    detail: 'If yes, describe the denial, revocation, or limitation and identify the issuing agency, the state, city, month and year issued.'
  },
  {
    id: 'q9',
    question: 'Have you ever requested a rehabilitation review from the Wisconsin DHS, a county department, private child placing agency, school board, or DHS-designated Tribe?',
    detail: 'If yes, indicate the agency that conducted the review, the outcome, month, and year of the review.'
  },
  {
    id: 'q10',
    question: 'Have you been discharged from a branch of the US Armed Forces, including any reserve component?',
    detail: 'If yes, indicate the month and year of discharge. Note: You may be required to provide a copy of your DD214 form.'
  },
  {
    id: 'q11',
    question: 'Have you resided outside of Wisconsin in the last three (3) years?',
    detail: 'If yes, list each state, city, and the dates you resided there.'
  },
  {
    id: 'q12',
    question: 'Are you applying or renewing eligibility to work as a government employee for the State of Wisconsin?',
    detail: 'If yes to Question 12 and you have resided outside of Wisconsin in the last seven (7) years, list each state, city, and dates.',
    hasSubQuestion: true,
    subQuestion: 'If yes above, have you resided outside of Wisconsin in the last seven (7) years?'
  },
];

export default function BackgroundCheckPage() {
  const navigate = useNavigate();
  const sigRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    // Type
    applicantType: 'applicant',
    otherType: '',
    positionDescription: '',
    
    // Applicant Info
    firstName: '',
    middleName: '',
    lastName: '',
    ssn: '',
    dateOfBirth: '',
    sex: '',
    phone: '',
    address: '',
    city: '',
    state: 'WI',
    zipCode: '',
    hasOtherNames: false,
    otherNames: '',
    
    // Employer Info (Pre-filled for Avida)
    employerName: 'Avida Personal Care',
    employerAddress: '790 N. Milwaukee St. Suite 355',
    employerCity: 'Milwaukee',
    employerState: 'WI',
    employerZip: '53202',
    
    // Disclosures (12 questions)
    q1: null, q1Detail: '',
    q2: null, q2Detail: '',
    q3: null, q3Detail: '',
    q4: null, q4Detail: '',
    q5: null, q5Detail: '',
    q6: null, q6Detail: '',
    q7: null, q7Detail: '',
    q8: null, q8Detail: '',
    q9: null, q9Detail: '',
    q10: null, q10Detail: '',
    q11: null, q11Detail: '',
    q12: null, q12Sub: null, q12Detail: '',
    
    // Attestation
    signatureData: null,
    dateSigned: new Date().toISOString().split('T')[0],
    certifyAccurate: false,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDisclosureChange = (qId, value) => {
    setFormData(prev => ({
      ...prev,
      [qId]: value,
      // Clear detail if switching to No
      ...(value === false && { [`${qId}Detail`]: '' })
    }));
  };

  const clearSignature = () => {
    if (sigRef.current) {
      sigRef.current.clear();
      setFormData(prev => ({ ...prev, signatureData: null }));
    }
  };

  const saveSignature = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const dataUrl = sigRef.current.toDataURL('image/png');
      setFormData(prev => ({ ...prev, signatureData: dataUrl }));
      toast.success('Signature captured');
    }
  };
  
  // Capture signature data right before submit if canvas has content
  const captureSignatureNow = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      return sigRef.current.toDataURL('image/png');
    }
    return formData.signatureData;
  };

  const nextStep = () => {
    if (currentStep < FORM_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const validateDisclosures = () => {
    for (const q of DISCLOSURE_QUESTIONS) {
      if (formData[q.id] === null) {
        return { valid: false, message: `Please answer question: "${q.question.substring(0, 50)}..."` };
      }
    }
    return { valid: true };
  };

  const handleSubmit = async () => {
    // Validate disclosures
    const validation = validateDisclosures();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    // Capture signature from canvas immediately before submit
    const signatureToSubmit = captureSignatureNow();
    
    if (!signatureToSubmit) {
      toast.error('Please sign the form');
      return;
    }

    if (!formData.certifyAccurate) {
      toast.error('Please certify that the information is accurate');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        signatureData: signatureToSubmit, // Use freshly captured signature
        submittedAt: new Date().toISOString(),
      };
      
      const res = await fetch(`${API_BASE}/api/employment/background-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (res.ok) {
        toast.success('Background check form submitted!');
        navigate('/employment/success', { state: { formType: 'background-check' } });
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (FORM_STEPS[currentStep].id) {
      case 'type':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold text-slate-800">Background Information Disclosure</h2>
                <p className="text-sm text-slate-500">Wisconsin Form F-82064</p>
              </div>
            </div>
            
            <Card className="p-4 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                This form is required by Wisconsin law for all individuals working with vulnerable populations.
                Providing false or incomplete information may result in criminal penalties.
              </p>
            </Card>

            <div>
              <Label className="text-base font-semibold mb-3 block">I am applying as a:</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['applicant', 'contractor', 'student', 'volunteer', 'household', 'other'].map(type => (
                  <label 
                    key={type} 
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.applicantType === type 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <input 
                      type="radio" 
                      name="applicantType" 
                      value={type}
                      checked={formData.applicantType === type}
                      onChange={e => handleChange('applicantType', e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="capitalize">{type === 'household' ? 'Household Member' : type === 'applicant' ? 'Applicant/Employee' : type}</span>
                  </label>
                ))}
              </div>
              {formData.applicantType === 'other' && (
                <Input 
                  className="mt-3" 
                  placeholder="Specify type..."
                  value={formData.otherType}
                  onChange={e => handleChange('otherType', e.target.value)}
                />
              )}
            </div>

            <div>
              <Label>Describe the position for which you are applying:</Label>
              <Input 
                value={formData.positionDescription} 
                onChange={e => handleChange('positionDescription', e.target.value)}
                placeholder="e.g., Personal Care Worker, CNA, Office Staff"
              />
            </div>
          </div>
        );

      case 'applicant':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Applicant Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={formData.firstName} onChange={e => handleChange('firstName', e.target.value)} required />
              </div>
              <div>
                <Label>Middle Name</Label>
                <Input value={formData.middleName} onChange={e => handleChange('middleName', e.target.value)} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={formData.lastName} onChange={e => handleChange('lastName', e.target.value)} required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Social Security Number *</Label>
                <Input value={formData.ssn} onChange={e => handleChange('ssn', e.target.value)} placeholder="XXX-XX-XXXX" required />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input type="date" value={formData.dateOfBirth} onChange={e => handleChange('dateOfBirth', e.target.value)} required />
              </div>
              <div>
                <Label>Sex *</Label>
                <div className="flex gap-4 mt-2">
                  {['Male', 'Female'].map(sex => (
                    <label key={sex} className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio" 
                        name="sex" 
                        value={sex}
                        checked={formData.sex === sex}
                        onChange={e => handleChange('sex', e.target.value)}
                        className="w-4 h-4"
                      />
                      <span>{sex}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Phone Number *</Label>
              <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="(XXX) XXX-XXXX" />
            </div>

            <div>
              <Label>Street Address *</Label>
              <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>City *</Label>
                <Input value={formData.city} onChange={e => handleChange('city', e.target.value)} />
              </div>
              <div>
                <Label>State *</Label>
                <Input value={formData.state} onChange={e => handleChange('state', e.target.value)} />
              </div>
              <div>
                <Label>ZIP Code *</Label>
                <Input value={formData.zipCode} onChange={e => handleChange('zipCode', e.target.value)} />
              </div>
            </div>

            <Card className="p-4 bg-slate-50">
              <div className="flex items-center gap-3 mb-3">
                <Checkbox 
                  checked={formData.hasOtherNames} 
                  onCheckedChange={v => handleChange('hasOtherNames', v)} 
                />
                <Label>I have used other names (including maiden name)</Label>
              </div>
              {formData.hasOtherNames && (
                <Input 
                  placeholder="List all other names used..."
                  value={formData.otherNames}
                  onChange={e => handleChange('otherNames', e.target.value)}
                />
              )}
            </Card>
          </div>
        );

      case 'employer':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Employer Information</h2>
            <p className="text-slate-500 text-sm">Organization requesting the background check</p>
            
            <Card className="p-4 bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-700">
                This information is pre-filled for Avida Personal Care
              </p>
            </Card>

            <div>
              <Label>Employer Name</Label>
              <Input value={formData.employerName} onChange={e => handleChange('employerName', e.target.value)} />
            </div>

            <div>
              <Label>Street Address</Label>
              <Input value={formData.employerAddress} onChange={e => handleChange('employerAddress', e.target.value)} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <Label>City</Label>
                <Input value={formData.employerCity} onChange={e => handleChange('employerCity', e.target.value)} />
              </div>
              <div>
                <Label>State</Label>
                <Input value={formData.employerState} onChange={e => handleChange('employerState', e.target.value)} />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input value={formData.employerZip} onChange={e => handleChange('employerZip', e.target.value)} />
              </div>
            </div>
          </div>
        );

      case 'disclosures':
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-800">Disclosure Questions</h2>
              <p className="text-slate-500 text-sm">Answer all questions truthfully. Failure to disclose may result in penalties.</p>
            </div>

            {DISCLOSURE_QUESTIONS.map((q, idx) => (
              <Card key={q.id} className="p-4" data-testid={`disclosure-${q.id}`}>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-semibold text-slate-600 shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-slate-700 mb-3">{q.question}</p>
                    
                    <div className="flex gap-4 mb-3">
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
                        formData[q.id] === true ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input 
                          type="radio" 
                          name={q.id}
                          checked={formData[q.id] === true}
                          onChange={() => handleDisclosureChange(q.id, true)}
                          className="w-4 h-4"
                        />
                        <span>Yes</span>
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
                        formData[q.id] === false ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 hover:border-slate-300'
                      }`}>
                        <input 
                          type="radio" 
                          name={q.id}
                          checked={formData[q.id] === false}
                          onChange={() => handleDisclosureChange(q.id, false)}
                          className="w-4 h-4"
                        />
                        <span>No</span>
                      </label>
                    </div>

                    {formData[q.id] === true && (
                      <div className="mt-3">
                        <Label className="text-sm text-slate-600">{q.detail}</Label>
                        <Textarea 
                          className="mt-2"
                          value={formData[`${q.id}Detail`]}
                          onChange={e => handleChange(`${q.id}Detail`, e.target.value)}
                          placeholder="Provide details..."
                          rows={3}
                        />
                      </div>
                    )}

                    {q.hasSubQuestion && formData[q.id] === true && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm font-medium text-slate-600 mb-2">{q.subQuestion}</p>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`${q.id}Sub`}
                              checked={formData[`${q.id}Sub`] === true}
                              onChange={() => handleChange(`${q.id}Sub`, true)}
                              className="w-4 h-4"
                            />
                            <span>Yes</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`${q.id}Sub`}
                              checked={formData[`${q.id}Sub`] === false}
                              onChange={() => handleChange(`${q.id}Sub`, false)}
                              className="w-4 h-4"
                            />
                            <span>No</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        );

      case 'attestation':
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-slate-800">Attestation & Signature</h2>
            
            <Card className="p-6 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-900 leading-relaxed">
                I certify that the information I have provided on this form is accurate and complete. 
                I understand that providing inaccurate or incomplete information may result in a denial 
                or reversal of a determination of my eligibility, or in criminal penalties under 
                Wis. Stat. § 48.685(9), 50.065(9), or another applicable provision.
              </p>
            </Card>

            <div className="flex items-start gap-3">
              <Checkbox 
                checked={formData.certifyAccurate} 
                onCheckedChange={v => handleChange('certifyAccurate', v)} 
                data-testid="certify-checkbox"
              />
              <Label className="text-sm leading-relaxed">
                I have read and understand the attestation above, and I certify that all information 
                provided is accurate and complete. *
              </Label>
            </div>

            <div>
              <Label className="mb-3 block">Signature *</Label>
              <Card className="p-4 bg-white">
                <div className="border-2 border-dashed border-slate-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={sigRef}
                    canvasProps={{
                      className: 'w-full h-40 bg-white',
                      'data-testid': 'signature-canvas'
                    }}
                    onEnd={saveSignature}
                  />
                </div>
                <div className="flex justify-between items-center mt-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={clearSignature}
                    data-testid="clear-signature-btn"
                  >
                    <Eraser className="w-4 h-4 mr-2" /> Clear
                  </Button>
                  {(formData.signatureData || (sigRef.current && !sigRef.current?.isEmpty?.())) && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Signature captured
                    </span>
                  )}
                </div>
              </Card>
            </div>

            <div>
              <Label>Date Signed</Label>
              <Input 
                type="date" 
                value={formData.dateSigned} 
                onChange={e => handleChange('dateSigned', e.target.value)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24">
      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer group" onClick={() => navigate('/employment')}>
            <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-[#0A368F] transition-colors" />
            <img src={AVIDA_LOGO} alt="Avida" className="h-10" />
          </div>
          <span className="badge-navy">Background Check</span>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          {/* Progress bar */}
          <div className="progress-gradient mb-4">
            <div 
              className="h-full transition-all duration-500" 
              style={{ width: `${((currentStep + 1) / FORM_STEPS.length) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between overflow-x-auto gap-2 scrollbar-hide">
            {FORM_STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(idx)}
                  data-testid={`step-${step.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    isActive ? 'step-active shadow-lg shadow-blue-200' :
                    isCompleted ? 'step-completed' :
                    'step-pending hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{step.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="card-luxury p-8 md:p-10">
          {renderStepContent()}
        </div>
      </main>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-100 p-4 safe-area-bottom">
        <div className="max-w-4xl mx-auto flex justify-between gap-4">
          <Button 
            variant="outline" 
            onClick={prevStep} 
            disabled={currentStep === 0}
            className="btn-outline-navy h-12 px-6"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          
          {currentStep === FORM_STEPS.length - 1 ? (
            <Button 
              onClick={handleSubmit} 
              disabled={isSubmitting} 
              className="btn-navy h-12 px-8"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" /> Submit Form</>
              )}
            </Button>
          ) : (
            <Button 
              onClick={nextStep} 
              className="btn-gold h-12 px-8"
              data-testid="next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
